/**
 * 商品冲调
 */
import React, { useEffect, useRef, memo } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button } from 'antd';
import intl from 'react-intl-universal';

import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';
import RemarkModal, { RemarkModalRef } from '@/components/Order/RemarkModal';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { initPageState, setLoadingStatus, getMainState } from '@/redux/actions/reversal/goods';
import { ReducerState, storeName } from '@/redux/reducers/reversal/goods';
import { submitGoodsReversal } from '@/api/goodsReversal';
import { BillItem } from '@/pages/bill/interface';

import GoodsList from './components/GoodsList';
import GoodsSummary from './components/GoodsSummary';
import SubmitButton from './components/SubmitButton';

import * as helper from './helper';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading } = state;
  return { isInit, isLoading };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitState: initPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IConfig {
  bill: BillItem;
  billNo: string; // 消费账单id
  resvNo: string; // 主单号
  onEnd?: (result: boolean) => void;
}
interface IProps extends IConfig, MapStateProps, MapDispatchProps {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const { unmountNode, bill, onEnd, isInit, isLoading, callSetLoading, callGetState } = props;
  const remarkModalRef = useRef<RemarkModalRef>(null);

  useEffect(() => {
    const { callInitState, billNo } = props;
    if (billNo) {
      callInitState(() => {
        return helper.getStateFromOrder(billNo).catch((e) => {
          unmountNode();
          throw e;
        });
      });
    } else {
      message.error(intl.get('reversal_goods_error_config_invalid'));
      unmountNode();
    }
  }, []);

  // 关闭回调
  const closeCallback = (modify: boolean) => {
    unmountNode();
    if (typeof onEnd === 'function') {
      onEnd(modify);
    }
  };

  // 取消操作
  const handleEditCancel = () => {
    closeCallback(false);
  };

  // 提交
  const handleSubmitClick = (billRemarkText) => {
    callGetState(async (state) => {
      callSetLoading(true);
      try {
        const requestData = helper.formatToSubmitData(state);
        requestData.no = props.billNo;
        requestData.remark = billRemarkText;
        requestData.resvNo = props.resvNo;
        await submitGoodsReversal(requestData);
        message.success(intl.get('reversal_goods_message_edit_success'));
        closeCallback(true);
      } catch (_) {
        callSetLoading(false);
      }
    });
  };

  // 账务备注弹框
  const showRemarkModal = () => {
    remarkModalRef.current.showModal();
  };

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader title={intl.get('reversal_goods_drawer_title')} onClose={handleEditCancel} />
      <div className={styles.pageContainer}>
        <GoodsList bill={bill} />
        <GoodsSummary />
      </div>
      <DrawerFooter>
        <SubmitButton onClick={showRemarkModal} />
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
      <RemarkModal ref={remarkModalRef} scene="GOODS_REVERSAL" onConfirm={handleSubmitClick} />
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));
